import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tree } from 'react-arborist';
import type { VoidFunctionComponent } from 'react';
import type { NodeApi, TreeApi } from 'react-arborist';

import { useParams, useQuery } from '../../../../contexts/RouterContext';
import { useAddToast } from '../../../../contexts/ToastContext';
import { getSnippetPaths, getSnippetPathsQueryKey } from '../../../../hooks/api/snippets/useSnippetPaths';
import { formatPathForId, formatSnippetPath } from '../utils';
import type { GetSnippetPathsParams, SnippetPath, SnippetsQueryParams } from '../../../../../shared/snippets';
import type { SnippetsRouteParams } from '../../../../hooks/api/snippets/shared';
import type { UseSnippetPathsOptions } from '../../../../hooks/api/snippets/useSnippetPaths';
import type { SnippetTreeNodeData } from '../types';

import SnippetsTreeViewNode from './Node';

/**
 * Fallback tree height used before the resizable layout has measured the tree
 * pane. react-arborist needs a numeric height to virtualize its rows.
 */
const FALLBACK_TREE_HEIGHT = 550;

/**
 * SAFETY LIMIT: Maximum depth for auto-expansion to prevent infinite loops and performance issues.
 *
 * This protects against:
 * - Malicious URLs with extremely deep paths (/a/b/c/d/.../z/file.txt)
 * - Infinite loops if there's a bug in the expansion logic
 * - Performance degradation from too many sequential API calls
 *
 * 10 levels should be sufficient for any reasonable project structure while being safe.
 * Examples that fit: /src/components/ui/forms/inputs/TextInput/TextInput.tsx (7 levels)
 */
const MAX_EXPANSION_DEPTH = 10;

/**
 * Function to recursively "inject" new children into the tree.
 *
 * @param {SnippetTreeNodeData[]} nodes - The nodes to inject into.
 * @param {string} parentPath - The path to the parent node.
 * @param {SnippetTreeNodeData[]} children - The new children to inject.
 * @returns {SnippetTreeNodeData[]} - The new tree with the children injected.
 */
function injectChildren(
  nodes: SnippetTreeNodeData[],
  parentPath: string,
  children: SnippetTreeNodeData[],
): SnippetTreeNodeData[] {
  return nodes.map((node) => {
    //  If the node's path matches the parent path, we need to inject the new children into it.
    if (node.path === parentPath) {
      return { ...node, children, hasLoaded: true };
    }
    return node.children
      ? {
          ...node,
          //  Recursively inject the new children into the node's children.
          children: injectChildren(node.children, parentPath, children),
        }
      : node;
  });
}

type Props = {
  rootPath: SnippetPath;
  userRevisionLocator: string;
  selectedPath: string;
  setSelectedPath: (path: string) => void;
  /** Width of the tree pane, measured by the resizable file-tree layout. */
  width?: number;
  /** Height of the tree pane, measured by the resizable file-tree layout. */
  height?: number;
};

/**
 * SnippetsTreeView renders the file tree for a given userRevisionLocator,
 * allowing the user to expand and load directories in a given file tree.
 * It will load the tree lazily, only loading the children of a directory when it is expanded.
 *
 * COMPLEXITY NOTE: This component contains one of the most complex features in our codebase:
 * AUTO-EXPANSION TO SELECTED PATH. When a user navigates to a page with a deep selectedPath
 * in the URL (e.g., /src/components/MyComponent.tsx), the tree automatically expands all
 * intermediate directories to show that path. This requires:
 * - Sequential API calls (can't parallelize due to dependencies)
 * - Coordination between React state, React Query cache, and react-arborist internal state
 * - Robust error handling for missing paths and API failures
 * - Prevention of infinite loops while allowing re-expansion
 * See the expandToSelectedPath function for detailed implementation comments.
 *
 * These results are cached via React Query, but because we can only pass a static component to react-arborist,
 * we aren't using React Query in the typical way here (instead we are manually calling the fetch function directly).
 *
 * react-arborist is also generally used with static lists of data, but because we are lazy-loading the tree when
 * a user expands the directory, we need to manually "inject" the new children into the tree at the right place.
 */
const SnippetsTreeView: VoidFunctionComponent<Props> = ({
  rootPath,
  userRevisionLocator,
  selectedPath,
  setSelectedPath,
  width,
  height,
}) => {
  const rootNode = useMemo(() => ({
    type: rootPath.type,
    name: rootPath.name,
    path: rootPath.path,
    count: rootPath.count,
    children: [],
    userRevisionLocator,
  }), [rootPath, userRevisionLocator]);

  //  We need the route and query params for fetching snippet paths.
  const routeParams = useParams<SnippetsRouteParams>();
  const [queryParams] = useQuery<SnippetsQueryParams>();

  const addToast = useAddToast();
  //  Keep a reference to the tree. This is so that we can properly mark nodes as open/closed in the onToggle.
  const treeRef = useRef<TreeApi<SnippetTreeNodeData>>(null);
  //  Initially, just the root is displayed.
  const [data, setData] = useState<SnippetTreeNodeData[]>([rootNode]);
  //  This is so that we can do flat lookups when we need to inject nodes.
  //  This doesn't need to be state because it doesn't really power any UI/we don't need the re-render.
  const nodesByPath = useRef<Record<string, SnippetTreeNodeData>>({
    [rootNode.path]: rootNode,
  });

  //  When the root node finishes loading, reset the data to ensure we have the latest info.
  //  This also resets the tree state (so all nodes are closed), whenever the root node changes.
  //  This happens whenever the current filters (including path) change.
  useEffect(() => {
    setData([rootNode]);
    nodesByPath.current[rootNode.path] = rootNode;
    treeRef.current?.closeAll();
  }, [rootNode]);

  //  This is an esoteric use of React Query, typically you should never need to use queryClient directly in this way.
  //  However, doing it like this allows us to retain the benefits of React Query (like the caching)
  //  while still being able to imperatively make a new query when a new directory is toggled.
  //  Don't copy this pattern unless you have tried doing things the "normal" way first.
  const queryClient = useQueryClient();

  //  This is a helper function to register the children in the nodesByPath ref.
  //  This is so that we can do flat lookups when we need to inject nodes.
  const registerChildren = (children: SnippetTreeNodeData[]) => {
    children.forEach((child) => {
      nodesByPath.current[child.path] = child;
    });
  };

  //  "inject" the newly loaded children into the tree and update the ref.
  const injectChildrenOnLoad = useCallback((parentPath: string, childNodes: SnippetTreeNodeData[]) => {
    //  Put each new child into the map
    registerChildren(childNodes);
    //  Now we need to update the parent entry with the updated object (so that it has the children).
    const parentNode = nodesByPath.current[parentPath];
    if (parentNode) {
      nodesByPath.current[parentPath] = {
        ...parentNode,
        children: childNodes,
        hasLoaded: true,
      };
    }
    //  Finally, we update the actual state data being passed to the Tree
    setData(prevData => injectChildren(prevData, parentPath, childNodes));
  }, []);

  //  Method for marking descendants of a node as "closed" within the react-arborist's internal state
  const closeDescendants = useCallback((node: SnippetTreeNodeData) => {
    const tree = treeRef?.current;
    if (tree) {
      node.children?.forEach(child => {
        tree.close(child.path);
        //  Recurse
        closeDescendants(child);
      });
    }
  }, [treeRef]);

  /**
   * ENHANCED DIRECTORY TOGGLE HANDLER
   *
   * This function handles both manual user clicks AND programmatic expansion (auto-expansion).
   *
   * @param arboristId - The react-arborist internal ID for the node
   * @param forceExpand - NEW PARAMETER: When true, forces expansion regardless of current state
   *                      - Used by auto-expansion to programmatically open directories
   *                      - Bypasses normal toggle behavior (open/close on click)
   *                      - Ensures directory opens even if user hasn't clicked it
   */
  const handleToggle = useCallback(async (arboristId: string, forceExpand = false) => {
    // Convert arborist ID back to raw path for lookup
    const rawPath = arboristId.endsWith('/') && arboristId !== '/' ? arboristId.slice(0, -1) : arboristId;

    //  Do a flat lookup to find the node.
    const node = nodesByPath.current[rawPath];

    //  Catch undefined, non-directory nodes
    if (!node || node.type !== 'directory') {
      return;
    }

    //  Situation 1: The folder has just been closed.
    //  In this case, we need to manually tell arborist that all of its descendants are also closed.
    const tree = treeRef?.current;
    //  Handle undefined (mostly for TypeScript)
    if (!tree) {
      return;
    }

    // ENHANCED EXPANSION LOGIC: Handle both manual clicks and programmatic expansion
    const isCurrentlyOpen = tree.isOpen(arboristId);
    const shouldExpand = forceExpand || isCurrentlyOpen;

    // PROGRAMMATIC EXPANSION: When auto-expanding, force the node to open visually
    // This is crucial because react-arborist's normal toggle behavior assumes user clicks
    // For auto-expansion, we need to ensure the node is open regardless of previous state
    if (forceExpand && !isCurrentlyOpen) {
      tree.open(arboristId);
    }

    if (!shouldExpand) {
      //  Tell arborist to close all the descendants
      closeDescendants(node);
      return;
    }

    //  Situation 2: The folder was just opened.
    //  In this case, we need to fetch the children of that node (if we haven't already).
    if (node.hasLoaded) {
      return;
    }

    const params: GetSnippetPathsParams = {
      ...queryParams,
      //  This is to fix an issue where arborist needs path to be non-empty but the backend expects "/" for root.
      path: node.name === rootNode.name ? '/' : rawPath,
    };

    const options: UseSnippetPathsOptions = {
      userRevisionLocator,
      params,
    };

    if (queryParams.olderRevisionLocator && routeParams.status) {
      options.compare = {
        olderRevisionLocator: queryParams.olderRevisionLocator,
        status: routeParams.status,
      };
    }

    //  `ensureQueryData` is an asynchronous function that can be used to get an existing query's cached data.
    //  If the query does not exist, queryClient.fetchQuery will be called and its results returned.
    const response = await queryClient.ensureQueryData({
      queryKey: getSnippetPathsQueryKey(options),
      queryFn: () => getSnippetPaths(options),
    });

    //  Handle error case by displaying a toast, then returning early before updating the tree.
    if (!response.data?.paths) {
      addToast({
        type: 'error',
        content: `Failed to load snippets for path: ${rawPath}`,
      });
      return;
    }

    //  "inject" the newly loaded children into the tree at the correct place, and update the ref.
    injectChildrenOnLoad(
      rawPath, // Use raw path for tree operations
      response.data.paths.map(path => ({
        ...path,
        //  If `react-arborist` is passed a node with `children` set, it will treat the node as a directory which can be expanded.
        //  We do not want this for cases where it is a file, so we set `children` to `undefined` in that case.
        //  Later when this directory is expanded, we will "inject" the children into the tree.
        children: path.type === 'directory' ? [] : undefined,
        userRevisionLocator,
      })),
    );
  }, [
    rootNode.name,
    queryClient,
    userRevisionLocator,
    injectChildrenOnLoad,
    closeDescendants,
    addToast,
    routeParams.status,
    queryParams,
  ]);

  //  Track which paths we've attempted to auto-expand to prevent infinite loops
  const expandedPathsRef = useRef<Set<string>>(new Set());

  /**
   * COMPLEX FEATURE: Auto-expansion to selectedPath
   *
   * This function solves a complex UX problem: when a user navigates to a page with a deep
   * selectedPath in the URL (e.g., /src/components/MyComponent.tsx), we need to automatically
   * expand all intermediate directories in the tree to show that path.
   *
   * THE CHALLENGE:
   * - Our tree loads lazily (only immediate children when expanded)
   * - The API only gives us one level at a time (/api/.../paths?path=/src gives us /src/children but not /src/nested/children)
   * - react-arborist requires nodes to exist before we can expand them
   * - We need to expand /src, then /src/components, then finally select /src/components/MyComponent.tsx
   *
   * THE SOLUTION:
   * 1. Always expand root first (required for any path to work)
   * 2. Process path segments sequentially (can't parallelize - each depends on the previous)
   * 3. For each segment: verify it exists, fetch if needed, expand in react-arborist
   * 4. Finally select/focus the target node
   *
   * WHY THIS IS COMPLEX:
   * - Must coordinate between: API calls, React state, react-arborist internal state
   * - Handle race conditions, missing paths, API errors gracefully
   * - Prevent infinite loops while allowing re-expansion when selectedPath changes
   * - Work with existing manual expansion logic without conflicts
   */
  const expandToSelectedPath = useCallback(async (targetPath: string) => {
    // GUARD CLAUSES: Early returns for invalid cases
    if (!targetPath || targetPath === '/' || expandedPathsRef.current.has(targetPath)) {
      return;
    }

    // PREVENT INFINITE LOOPS: Mark this path as being processed
    // This is crucial because React effects can trigger multiple times
    expandedPathsRef.current.add(targetPath);

    // STEP 1: ENSURE ROOT IS EXPANDED
    // This is absolutely critical! Without root being expanded, we have no children to work with.
    // The tree starts with just a root node that has no children loaded.
    const rootNodePath = rootNode.path;
    const rootNodeInPath = nodesByPath.current[rootNodePath];
    if (!rootNodeInPath?.hasLoaded) {
      try {
        const rootArboristId = formatPathForId(rootNodePath, 'directory');
        // We use forceExpand=true to ensure root opens even if not manually clicked
        await handleToggle(rootArboristId, true);
      } catch (error) {
        addToast({
          type: 'error',
          content: 'Failed to expand root directory',
        });
        return; // Can't continue without root expanded
      }
    }

    // STEP 2: PREPARE PATH SEGMENTS
    // Convert "/src/components/MyComponent.tsx" → ["src", "components", "MyComponent.tsx"]
    // We filter out empty strings to handle cases like "//path" or "/path/"
    const segments = targetPath.split('/').filter(Boolean);

    // STEP 3: SEQUENTIAL EXPANSION - THE CORE ALGORITHM
    // We MUST do this sequentially because each step depends on the previous:
    // - Can't expand /src/components until /src is loaded
    // - Can't verify /src/components exists until /src's children are fetched
    // - Each API call populates nodesByPath with new nodes we need for the next iteration
    //
    // This is why we can't parallelize and why we need await-in-loop (hence the eslint-disable)
    for (let i = 0; i < Math.min(segments.length, MAX_EXPANSION_DEPTH); i++) {
      // BUILD CURRENT PATH: For iteration i, build path up to that segment
      // i=0: /src, i=1: /src/components, i=2: /src/components/MyComponent.tsx
      const currentSegments = segments.slice(0, i + 1);
      const currentPath = `/${currentSegments.join('/')}`;

      try {
        // VALIDATION: Does this path segment actually exist in its parent?
        // This prevents us from trying to expand non-existent paths
        const parentPath = i === 0 ? '/' : `/${currentSegments.slice(0, -1).join('/')}`;
        const parentNode = nodesByPath.current[parentPath];
        const currentSegment = segments[i];

        if (!parentNode?.children?.some(child => child.name === currentSegment)) {
          // PATH NOT FOUND: This is a normal case - user might have typed a bad URL
          addToast({
            type: 'info',
            content: `Path not found: ${targetPath}. "${currentSegment}" not found in ${parentPath}`,
          });
          break; // Stop expansion, don't continue to non-existent paths
        }

        // SANITY CHECK: The node should exist in our lookup after parent expansion
        // If this fails, there's a bug in our tree state management
        const currentNode = nodesByPath.current[currentPath];
        if (!currentNode) {
          addToast({
            type: 'error',
            content: `Node not found in tree: ${currentPath}`,
          });
          break;
        }

        // EXPANSION DECISION: Should we expand this node?
        // - Always expand intermediate directories (need their children for next iteration)
        // - For final segment: only expand if it's a directory (files can't be expanded)
        const isDirectory = currentNode.type === 'directory';
        const isLastSegment = i === segments.length - 1;
        const shouldExpand = !isLastSegment || (isLastSegment && isDirectory);

        if (shouldExpand && !currentNode.hasLoaded) {
          // CASE A: Directory needs fetching and expanding
          const arboristId = formatPathForId(currentPath, 'directory');
          // Use forceExpand=true for programmatic expansion (vs user clicks)
          // eslint-disable-next-line no-await-in-loop
          await handleToggle(arboristId, true);
        } else if (shouldExpand && currentNode.hasLoaded) {
          // CASE B: Directory already fetched, just ensure it's visually open
          // This happens when we've previously manually expanded this directory
          const arboristId = formatPathForId(currentPath, 'directory');
          const tree = treeRef?.current;
          if (tree && !tree.isOpen(arboristId)) {
            tree.open(arboristId);
          }
        }
        // CASE C: Final file node - no expansion needed, just continue to selection step
      } catch (error) {
        // ROBUST ERROR HANDLING: If any step fails, stop gracefully
        // This prevents cascade failures and gives user actionable feedback
        addToast({
          type: 'error',
          content: `Failed to expand path: ${currentPath}`,
        });
        break;
      }
    }

    // STEP 4: FINAL SELECTION AND FOCUSING
    // After all the expansion is complete, we need to:
    // 1. Select the target node in react-arborist (highlights it)
    // 2. Focus it (ensures it's keyboard-accessible and visible)
    //
    // This is the UX payoff - user sees their selected path highlighted in the tree
    try {
      const finalNode = nodesByPath.current[targetPath];
      if (finalNode) {
        // Convert to react-arborist's internal ID format (handles directory trailing slashes)
        const finalArboristId = formatPathForId(targetPath, finalNode.type);
        const tree = treeRef?.current;

        if (tree) {
          // SELECT: Highlights the node (usually with different background color)
          tree.select(finalArboristId);

          // FOCUS: Makes the node keyboard-accessible and ensures it's scrolled into view
          // This is crucial for deep trees where the target might be off-screen
          tree.focus(finalArboristId);
        }
      }
    } catch (error) {
      // GRACEFUL DEGRADATION: Selection/focusing is nice-to-have, not critical
      // If this fails, the expansion still worked, so don't show error to user
      // We could add a toast here if needed, but it might be too noisy for such edge cases
    }
  }, [handleToggle, addToast, treeRef, rootNode.path]);

  /**
   * AUTO-EXPANSION TRIGGER
   *
   * This useEffect is the entry point for auto-expansion. It runs when:
   * 1. Component first mounts with a selectedPath
   * 2. selectedPath changes (user navigates to different path)
   *
   * We clear the cache on each selectedPath change because:
   * - User might navigate from /src/A.tsx to /src/B.tsx (should re-expand)
   * - The cache prevents infinite loops within a single expansion, not across different ones
   */
  useEffect(() => {
    // RESET CACHE: Allow expansion to the new selectedPath even if we've tried others
    expandedPathsRef.current.clear();

    // TRIGGER EXPANSION: Only for non-root paths (root is always visible)
    if (selectedPath && selectedPath !== '/') {
      expandToSelectedPath(selectedPath);
    }
  }, [selectedPath, expandToSelectedPath]);

  return (
    <Tree<SnippetTreeNodeData>
      ref={treeRef}
      data={data}
      openByDefault={false}
      //  To determine the react-arborist internal ID for this node,
      //  we need to ensure that the trailing slash on directories is correct.
      idAccessor={(node) => formatPathForId(node.path, node.type)} // Custom ID accessor for consistent directory IDs
      childrenAccessor="children" // Tells it we want to use the "children" as the children
      onToggle={handleToggle}
      //  Layout. Width and height come from the resizable file-tree layout.
      rowHeight={32}
      indent={8}
      width={width}
      height={height ?? FALLBACK_TREE_HEIGHT}
      //  Selection logic
      selection={selectedPath}
      onSelect={(nodes: Array<NodeApi<SnippetTreeNodeData>>) => {
        //  react-arborist allows mutli-select, which is why it passes us an array of nodes.
        //  We only want to select the first node, so we take the first one.
        const node = nodes[0];
        if (node) {
          //  Format the path so that directories end in a trailing slash.
          setSelectedPath(formatSnippetPath(node));
        }
      }}
      disableMultiSelection
      disableDrag
    >
      {SnippetsTreeViewNode}
    </Tree>
  );
};

export default SnippetsTreeView;
